import React from 'react';

import {
  OmakasePolymorphicComponent,
  OmakasePolymorphicComponentProps,
  OmakasePolymorphicRootComponent,
} from '../utils/OmakasePolymorphicComponent';
import { ResponsiveParameter } from 'omakase/utils/responsiveSizeMap';
import { dividerBase } from './dividerCssDefs';

export type DividerPosition = 'top' | 'bottom' | 'middle';
export type DividerVariant = 'thin' | 'thick' | 'responsive';

export interface DividerProps {
  /**
   * Whether or not the item's vertical axis has increased margin
   */
  tall?: boolean;
  /**
   * Position of divider
   */
  position?: DividerPosition;
  /**
   * Whether the divider renders a "thin" (1px) or "thick" (12px) divider
   */
  variant?: DividerVariant;
  /**
   * Top margin, in theme spacing units
   */
  mt?: ResponsiveParameter<number>;
  /**
   * Bottom margin, in theme spacing units
   */
  mb?: ResponsiveParameter<number>;
}

export const DividerDefaultElement = 'div';
type DividerTypeGuard = 'div' | 'li';

const Divider: OmakasePolymorphicComponent<
  DividerProps,
  typeof DividerDefaultElement,
  DividerTypeGuard
> = React.forwardRef(
  <E extends React.ElementType = typeof DividerDefaultElement>(
    {
      component,
      position,
      tall = false,
      variant = 'thin',
      mt,
      mb,
      ...rest
    }: OmakasePolymorphicComponentProps<DividerProps, E, DividerTypeGuard>,
    ref: React.Ref<HTMLDivElement> | undefined
  ) => {
    const Component: React.ElementType = component || DividerDefaultElement;

    return (
      <OmakasePolymorphicRootComponent
        aria-hidden="true"
        css={dividerBase({ isTall: tall, position, variant, mt, mb })}
        ref={ref}
        component={Component}
        {...rest}
      />
    );
  }
);

export default Divider;
